/**
 * Store Registry
 *
 * Global singleton for type-erased store discovery and dispatch.
 * Enables AI Assistant and cross-store coordination by providing
 * a centralized registry of all active CDSE stores.
 */

import { useEffect } from 'react';
import { CommandMetaInput } from './types';
import { StoreNotMountedError } from './errors';

export interface StoreRegistration {
  name: string;
  getState: () => unknown;
  dispatch: ( command: any, meta: CommandMetaInput ) => void;
}

const registry = new Map<string, StoreRegistration>();

// Navigation function registry (for AI plan executor)
let navigateFn: (( path: string ) => void ) | null = null;

/**
 * Register a navigate function for use by the plan executor.
 * Call from a component that has access to React Router's useNavigate.
 */
export function registerNavigate( fn: ( path: string ) => void ): () => void {
  navigateFn = fn;
  return () => { navigateFn = null; };
}

/**
 * Get the registered navigate function.
 */
export function getNavigate(): (( path: string ) => void ) | null {
  return navigateFn;
}

/**
 * Register a store. Returns an unregister function.
 */
export function registerStore( registration: StoreRegistration ): () => void {
  registry.set( registration.name, registration );
  return () => {
    registry.delete( registration.name );
  };
}

/**
 * Get all registered stores.
 */
export function getAllStores(): StoreRegistration[] {
  return Array.from( registry.values());
}

/**
 * Build a snapshot of all store states (for AI context).
 */
export function buildContextSnapshot(): Record<string, unknown> {
  const snapshot: Record<string, unknown> = {};
  for ( const [ name, reg ] of registry ) {
    snapshot[name] = reg.getState();
  }
  return snapshot;
}

/**
 * Dispatch a command to a specific store by name.
 * Throws StoreNotMountedError if the store is not registered.
 */
export function dispatchToStore( name: string, command: any, meta: CommandMetaInput ): void {
  const store = registry.get( name );
  if ( !store ) {
    throw new StoreNotMountedError( name, command.type );
  }
  store.dispatch( command, meta );
}

/**
 * React hook to register a store while a component is mounted.
 * Call alongside usePolicies in page-level components.
 */
export function useRegisterStore(
  name: string,
  getState: () => unknown,
  dispatch: ( command: any, meta: CommandMetaInput ) => void
): void {
  useEffect(() => {
    const unregister = registerStore({ name, getState, dispatch });
    return unregister;
  }, [name]);
}
